import { ClipboardEventHandler, cloneElement, FunctionComponent, KeyboardEventHandler, useRef, useState } from 'react'
import { Controller, FieldPath, FieldValues, UseControllerProps } from 'react-hook-form'
import { Combobox, useCombobox } from '@mantine/core'

import { Color, Colors, ColorUtil } from '../style/colors'
import { Fonts } from '../style/fonts'
import { WithClassName } from '../types/utils'
import { useMergedRef } from '@mantine/hooks'
import { fastHash } from '../utils/string'
import { Chip, ChipProps } from './Chip'
import { BaseInput, BaseInputProps } from './Input'
import { TrackingLabel } from '../types/tracking'
import { StatusState } from './Status'

const KEYCODE = {
  SPACE: 'Space',
  ENTER: 'Enter',
  NUMPADENTER: 'NumpadEnter',
  DELETE: 'Backspace',
} as const

const chipListStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
  alignItems: 'center',
  padding: '10px 0',
}

const inputStyle: React.CSSProperties = {
  flex: 1,
  minWidth: '60px',
  border: 'none',
  outline: 'none',
}

const dropdownStyle: React.CSSProperties = {
  maxHeight: 200,
  overflowY: 'scroll',
  scrollbarWidth: 'thin',
}

const groupLabelStyle: React.CSSProperties = {
  ...Fonts.P3,
  ...Fonts.colors.SlateGrey,
  fontWeight: 400,
}

const noResultStyle: React.CSSProperties = {
  padding: '8px 12px',
  color: Colors.SlateGrey,
  fontStyle: 'italic',
}

export type ChipListChipRenderer = (chip: ChipProps, ctx: { value: string; index: number }) => React.ReactElement<ChipProps>

export type ChipSelectableOptionProp<T = unknown> = {
  value: T
  label: string
  trackingLabel: TrackingLabel
  status?: StatusState
  sublabel?: string
  disabled?: boolean
}

export type ChipSelectableOptionGroupProp<T = unknown> = {
  label: string
  options: ChipSelectableOptionProp<T>[]
}

const isOptionGroup = (option: ChipSelectableOptionProp | ChipSelectableOptionGroupProp): option is ChipSelectableOptionGroupProp => 'options' in option

type ChipListInputProps<T extends FieldValues, U extends FieldPath<T>> = WithClassName &
  Omit<BaseInputProps, 'value' | 'onChange'> &
  Omit<UseControllerProps<T, U>, 'defaultValue'> & {
    maxChipCount?: number
    chipColor?: Color
    isColorVariable?: boolean
    colorVariability?: number
    chipRenderer?: ChipListChipRenderer
    isSpaceAllowed?: boolean
    canRemoveValues?: boolean
    isPasteAllowed?: boolean
    selectableOptions?: (ChipSelectableOptionProp | ChipSelectableOptionGroupProp)[]
    OptionContentComponent?: FunctionComponent<{ option: ChipSelectableOptionProp<unknown> }>
    displayLabel?: boolean
    onChange?: (values: string[]) => void
  }

export const ChipListInput = <T extends FieldValues, U extends FieldPath<T>>({
  name,
  control,
  rules,
  shouldUnregister,
  maxChipCount = 100,
  chipColor,
  isColorVariable,
  colorVariability,
  chipRenderer,
  isSpaceAllowed,
  canRemoveValues = true,
  isPasteAllowed = true,
  selectableOptions,
  OptionContentComponent,
  displayLabel,
  onChange: externalOnChange,
  ...props
}: ChipListInputProps<T, U>) => {
  const internalRef = useRef<HTMLInputElement>(null)
  const combobox = useCombobox()

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      shouldUnregister={shouldUnregister}
      render={({ field: { ref, value = [], onChange } }) => {
        // oxlint-disable-next-line react/hooks -- hooks live in this Controller render prop rather than a component, so the compiler skips it. Extracting the body into its own component is the real fix and is tracked separately.
        const [inputValue, setInputValue] = useState('')

        const chips = value as string[]

        // ungrouped options are handled as single-option groups rendered without a group label
        const optionGroups = selectableOptions?.map((o) => (isOptionGroup(o) ? o : { label: undefined, options: [o] })) ?? []

        const flatOptions = optionGroups.flatMap((group) => group.options)

        const filteredGroups = optionGroups
          .map((group) => ({ ...group, options: group.options.filter((opt) => !chips.includes(opt.value as string) && (!inputValue || opt.label.toLowerCase().includes(inputValue.toLowerCase()))) }))
          .filter((group) => group.options.length > 0)

        const getLabel = (val: string) => flatOptions.find((o) => o.value === val)?.label ?? val

        const getColor = (val: string) => {
          if (!chipColor) return Colors.Platinum
          if (!isColorVariable) return chipColor
          return ColorUtil.varyColorLightness(chipColor, fastHash(val) % (colorVariability ?? 30))
        }

        const update = (newChips: string[]) => {
          onChange(newChips)
          externalOnChange?.(newChips)
        }

        const addChip = (val: string) => {
          if (!val.trim() || chips.length >= maxChipCount) return
          update([...chips, val])
          setInputValue('')
        }

        const deleteChip = (index: number) => {
          const next = chips.filter((_, i) => i !== index)
          update(next)
        }

        const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
          if ([KEYCODE.ENTER, KEYCODE.NUMPADENTER].includes(e.code as any)) {
            e.preventDefault()
            const exactMatch = flatOptions.find((opt) => opt.label.toLowerCase() === inputValue.toLowerCase() || (opt.value as string).toLowerCase() === inputValue.toLowerCase())
            if (exactMatch && !exactMatch.disabled) {
              addChip(exactMatch.value as string)
            }
          }

          if (e.code === KEYCODE.DELETE && !inputValue) {
            deleteChip(chips.length - 1)
          }
        }

        const handlePaste: ClipboardEventHandler<HTMLInputElement> = (e) => {
          if (!isPasteAllowed) return
          e.preventDefault()
          e.clipboardData
            .getData('text')
            .split(/[\r\n\t]+/)
            .forEach(addChip)
        }

        const input = (
          <BaseInput
            {...props}
            // oxlint-disable-next-line react/hooks -- same Controller render prop as above: useMergedRef is a hook call outside a component
            ref={useMergedRef(internalRef, ref)}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            autoComplete="off"
            label={displayLabel && inputValue === '' && chips.length === 0 ? props.label : undefined}
            isClearable={canRemoveValues && chips.length > 0}
            onClear={() => update([])}
            internalInputRenderer={(innerInput: any) => (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={chipListStyle}>
                  {chips.map((val, i) =>
                    chipRenderer ? (
                      cloneElement(chipRenderer({ text: val, color: getColor(val) } as ChipProps, { value: val, index: i }), { key: i })
                    ) : (
                      <Chip
                        key={i}
                        text={getLabel(val)}
                        color={getColor(val)}
                        iconRight={
                          canRemoveValues
                            ? {
                                name: 'close',
                                clickable: {
                                  onClick: () => deleteChip(i),
                                  trackingLabel: `pbs-chip-delete-${i}-unset`,
                                },
                              }
                            : undefined
                        }
                      />
                    )
                  )}
                  {cloneElement(innerInput, { style: inputStyle })}
                </div>
              </div>
            )}
          />
        )

        if (!selectableOptions) return input

        return (
          <Combobox
            store={combobox}
            onOptionSubmit={(val) => {
              addChip(val)
              combobox.closeDropdown()
            }}
          >
            <Combobox.Target>
              <div onClick={() => combobox.openDropdown()}>{input}</div>
            </Combobox.Target>

            <Combobox.Dropdown>
              <Combobox.Options style={dropdownStyle}>
                {filteredGroups.length === 0 && <span style={noResultStyle}>No results match your search.</span>}
                {filteredGroups.map((group, groupIndex) => {
                  const options = group.options.map((opt) => (
                    <Combobox.Option key={opt.value as string} value={opt.value as string} disabled={opt.disabled}>
                      {OptionContentComponent ? <OptionContentComponent option={opt} /> : opt.label}
                    </Combobox.Option>
                  ))

                  return group.label === undefined ? (
                    options
                  ) : (
                    <Combobox.Group key={group.label ?? groupIndex} label={group.label} styles={{ groupLabel: groupLabelStyle }}>
                      {options}
                    </Combobox.Group>
                  )
                })}
              </Combobox.Options>
            </Combobox.Dropdown>
          </Combobox>
        )
      }}
    />
  )
}
